import { cn } from "@/design-system/lib/utils";
import type { WorkTaskStatus } from "@/api/workTasks";

interface Props {
  status: WorkTaskStatus;
  className?: string;
  onClick?: () => void;
  ariaLabel?: string;
  /**
   * Tasks Module v2 — when set on an In-progress pill, renders
   * "IN PROGRESS – 67%". Ignored for Open / Done.
   */
  percent?: number | null;
}

const STYLE: Record<WorkTaskStatus, string> = {
  // Spec § 7.7 — design system tokens only.
  Open: "bg-gray-mid text-onyx",
  InProgress: "bg-gold text-onyx",
  Done: "bg-status-success text-white-pure",
};

const LABEL: Record<WorkTaskStatus, string> = {
  Open: "Open",
  InProgress: "In progress",
  Done: "Done",
};

export function TaskStatusPill({ status, className, onClick, ariaLabel, percent }: Props) {
  const base =
    "inline-flex items-center px-space-2 py-[3px] rounded-sm " +
    "font-display text-[10px] font-bold uppercase tracking-[0.16em]";
  const label =
    status === "InProgress" && percent !== undefined && percent !== null
      ? `${LABEL[status]} – ${percent}%`
      : LABEL[status];
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-label={ariaLabel ?? `Status: ${label}. Click to advance.`}
        className={cn(base, STYLE[status], "hover:opacity-90", className)}
      >
        {label}
      </button>
    );
  }
  return (
    <span className={cn(base, STYLE[status], className)} aria-label={`Status: ${label}`}>
      {label}
    </span>
  );
}

/** Open → InProgress → Done → Open. Used by the clickable pill. */
export function nextStatus(status: WorkTaskStatus): WorkTaskStatus {
  if (status === "Open") return "InProgress";
  if (status === "InProgress") return "Done";
  return "Open";
}
